'use client';

import { ChevronIcon } from '@/components/ui';
import CloseIcon from '@/components/ui/Icons/CloseIcon';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackBannerViewedEvent,
  trackBannerClickedEvent,
  trackBannerDismissedEvent,
} from '@/utils/rudderstack';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export interface PlayKitSeenValues {
  title: string;
  image: string;
  link: string;
}

const PickUpWhereYouLeftOffBanner = () => {
  const [isOpen, setIsOpen] = useState<boolean>(
    localStorage.getItem(LOCAL_STORAGE_KEYS.isBannerOpen) === 'true',
  );
  const [playKitSeen, setPlayKitSeen] = useState<PlayKitSeenValues | null>(
    null,
  );

  useEffect(() => {
    const playKitSeenData = localStorage.getItem(
      LOCAL_STORAGE_KEYS.playKitSeen,
    );
    const hasUserLeft = localStorage.getItem(LOCAL_STORAGE_KEYS.hasUserLeft);

    if (hasUserLeft && playKitSeenData) {
      setIsOpen(true);
      localStorage.setItem(LOCAL_STORAGE_KEYS.isBannerOpen, 'true');
      trackBannerViewedEvent({
        type: CLICKED_ITEM_TYPES.VIEW,
        cta: CTA_NAMES.PICK_UP_WHERE_YOU_LEFT_OFF,
        pageLocation: PAGE_LOCATIONS.HOMEPAGE,
        url: window.location.href,
      });
    }

    if (playKitSeenData) {
      try {
        const parsedData: PlayKitSeenValues = JSON.parse(playKitSeenData);
        setPlayKitSeen(parsedData);
      } catch (error) {
        console.error('Failed to parse playKitSeenData:', error);
      }
    }

    localStorage.removeItem(LOCAL_STORAGE_KEYS.hasUserLeft);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.hasUserLeft, 'true');
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.hasUserLeft);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleStorageChange = () => {
      const bannerOpen = localStorage.getItem(LOCAL_STORAGE_KEYS.isBannerOpen);
      setIsOpen(bannerOpen === 'true');
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleDismiss = () => {
    setIsOpen(false);
    localStorage.setItem(LOCAL_STORAGE_KEYS.isBannerOpen, 'false');
    localStorage.removeItem(LOCAL_STORAGE_KEYS.playKitSeen);
    trackBannerDismissedEvent({
      type: CLICKED_ITEM_TYPES.DISMISS,
      cta: CTA_NAMES.PICK_UP_WHERE_YOU_LEFT_OFF,
      pageLocation: PAGE_LOCATIONS.HOMEPAGE,
      url: window.location.href,
    });
  };

  const handleBannerClicked = () => {
    trackBannerClickedEvent({
      type: CLICKED_ITEM_TYPES.LINK,
      cta: CTA_NAMES.PICK_UP_WHERE_YOU_LEFT_OFF,
      pageLocation: PAGE_LOCATIONS.HOMEPAGE,
      url: window.location.href,
    });
  };

  if (!isOpen || !playKitSeen) {
    return null;
  }

  return (
    <div className="w-[100%] bg-off-white shadow-lg lg:rounded-md p-2 lg:p-4">
      <button
        className="absolute top-3 lg:top-4 right-3 lg:right-4 text-lg font-bold"
        onClick={handleDismiss}
      >
        <CloseIcon />
      </button>
      <div className="flex gap-2 lg:gap-4 items-center pl-2 lg:pl-0">
        <div className="w-[25%] min-h-[70px] flex justify-center items-center">
          <Image
            src={playKitSeen?.image}
            alt={playKitSeen?.title}
            width={200}
            height={200}
          />
        </div>
        <div className="w-[75%] flex flex-col gap-0 lg:gap-2">
          <h3 className="text-[16px] lg:text-xl font-bold">
            Continue where you left off
          </h3>
          <Link
            href={playKitSeen?.link}
            className="text-blue-deep font-bold text-[14px] lg:text-base flex items-center"
            onClick={handleBannerClicked}
          >
            Shop {playKitSeen?.title}{' '}
            <ChevronIcon
              direction="right"
              classes="block lg:hidden ml-2"
              width={10}
              height={10}
            />
            <ChevronIcon
              direction="right"
              classes="hidden lg:block ml-2"
              width={14}
              height={14}
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PickUpWhereYouLeftOffBanner;
